/**
 * Embed bridge
 * ============
 * postMessage bridge between the /sdk/embed page and its host.
 *
 * Hosts:
 * - iframe: the page is framed by a web host (trustgate-embed.js loader).
 * - native: the page runs in a React Native WebView (window.ReactNativeWebView).
 *
 * Contract (outgoing): {source: "trustgate", type, payload}
 *   ready, step, complete, cancel, error, abandon
 * Contract (incoming): {source: "trustgate-host", type: "init", token, theme?}
 *
 * The SDK token arrives by postMessage and stays in memory only.
 * It is never written to the URL or to browser storage.
 */

export const OUTGOING_SOURCE = 'trustgate';
export const INCOMING_SOURCE = 'trustgate-host';

export function isNativeHost() {
  return typeof window !== 'undefined' && !!window.ReactNativeWebView;
}

export function isFramed() {
  try {
    return typeof window !== 'undefined' && window.parent !== window;
  } catch (e) {
    return true;
  }
}

/**
 * Create a bridge. `targetOrigin` starts as null (only the "ready" ping goes
 * out with "*", and it carries no data). Call lockOrigin() once the host is
 * known; after that every event goes only to that origin.
 */
export function createEmbedBridge() {
  const native = isNativeHost();
  let targetOrigin = null;

  const send = (type, payload) => {
    const event = { source: OUTGOING_SOURCE, type, payload: payload || {} };
    try {
      if (native) {
        window.ReactNativeWebView.postMessage(JSON.stringify(event));
        return;
      }
      if (!isFramed()) return;
      if (type === 'ready') {
        // The ready ping carries no data, so a wildcard origin is safe here.
        window.parent.postMessage({ source: OUTGOING_SOURCE, type: 'ready', payload: {} }, '*');
        return;
      }
      if (!targetOrigin) return; // never send data before the host origin is known
      window.parent.postMessage(event, targetOrigin);
    } catch (e) {
      // Best effort: a closed host must never break the flow.
    }
  };

  return {
    native,
    send,
    lockOrigin(origin) {
      if (origin && origin !== 'null') targetOrigin = origin;
    },
    getOrigin() {
      return targetOrigin;
    },
  };
}

/**
 * Parse and validate an incoming init message.
 * Returns {token, theme, parentOrigin} or null when the message must be ignored.
 */
export function parseInitMessage(event, { native }) {
  let data = event?.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      return null;
    }
  }
  if (!data || typeof data !== 'object') return null;
  if (data.source !== INCOMING_SOURCE || data.type !== 'init') return null;
  if (typeof data.token !== 'string' || !data.token) return null;

  if (native) {
    // RN injects via window.postMessage in the page itself, so the source is
    // this window (or null for the RN postMessage API). A framed page is not
    // a native host.
    if (isFramed()) return null;
    if (event.source && event.source !== window) return null;
  } else {
    // iframe mode: accept init only from the direct parent window.
    if (!isFramed() || event.source !== window.parent) return null;
    if (!event.origin || event.origin === 'null') return null;
  }

  const theme = data.theme === 'dark' || data.theme === 'light' ? data.theme : undefined;
  return {
    token: data.token,
    theme,
    parentOrigin: native ? null : event.origin,
  };
}

/**
 * Build the SDK API base URL the same way SDKVerifyPage does.
 */
export function getSdkApiBase() {
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  if (apiBase.endsWith('/api/v1')) return `${apiBase}/sdk`;
  return `${apiBase.replace(/\/$/, '')}/api/v1/sdk`;
}

/**
 * POST /api/v1/sdk/embed/session
 * The token goes in the Authorization header, the same way the SDK client sends it.
 */
export async function createEmbedSession(sdkBase, token, { parentOrigin, native }) {
  let res;
  try {
    res = await fetch(`${sdkBase}/embed/session`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ parent_origin: parentOrigin, native: !!native }),
    });
  } catch (e) {
    const err = new Error('Network error. Check your connection and try again.');
    err.code = 'network_error';
    err.retryable = true;
    throw err;
  }

  if (res.status === 403) {
    const err = new Error('This verification cannot be opened here.');
    err.code = 'origin_not_allowed';
    err.retryable = false;
    throw err;
  }
  if (res.status === 401) {
    const err = new Error('This verification session has expired. Please start again.');
    err.code = 'session_invalid';
    err.retryable = false;
    throw err;
  }
  if (!res.ok) {
    const err = new Error('We could not start the verification. Please try again.');
    err.code = res.status >= 500 ? 'server_error' : 'session_error';
    err.retryable = res.status >= 500;
    throw err;
  }
  return res.json();
}

/**
 * Map the submit outcome to the public complete status.
 */
export function toCompleteStatus(outcome) {
  if (outcome === 'approved' || outcome === 'rejected' || outcome === 'review') return outcome;
  return 'submitted';
}
